import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { apiService } from '../services/apiService';
import './Auth.css';

const Dashboard: React.FC = () => {
  const { user, isAuthenticated, logout, updateUser } = useAuth();
  const { language, t } = useLanguage();
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    firstName: '',
    email: '',
    phone: '',
    language: '',
  });

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    // Load user data into form
    if (user) {
      setFormData({
        name: user.name || '',
        firstName: user.firstName || '',
        email: user.email || '',
        phone: user.phone || '',
        language: user.language || language,
      });
    }
  }, [user, isAuthenticated, navigate, language]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const result = await apiService.updateUser({
        email: formData.email,
        name: formData.name,
        firstName: formData.firstName,
        phone: formData.phone,
        language: formData.language,
      });

      if (result.success && result.user) {
        setSuccess(t('dashboard.updateSuccess') || 'Profile updated successfully!');
        setIsEditing(false);
        // Update the user in context
        updateUser(result.user);
      } else {
        setError(result.error || (t('dashboard.updateError') || 'Failed to update profile'));
      }
    } catch (error) {
      console.error('Update error:', error);
      setError(t('dashboard.updateError') || 'An error occurred while updating your profile');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (user) {
      setFormData({
        name: user.name || '',
        firstName: user.firstName || '',
        email: user.email || '',
        phone: user.phone || '',
        language: user.language || language,
      });
    }
    setIsEditing(false);
    setError('');
    setSuccess('');
  };

  if (!isAuthenticated || !user) {
    return null;
  }

  return (
    <div className="auth-container">
      <div className="auth-card" style={{ maxWidth: '600px' }}>
        <h2>{t('dashboard.title') || 'My Dashboard'}</h2>
        
        {success && <div className="success-message" style={{ 
          backgroundColor: '#efe', 
          color: '#3c3', 
          padding: '0.75rem', 
          borderRadius: '5px', 
          marginBottom: '1rem', 
          textAlign: 'center' 
        }}>{success}</div>}
        
        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="email">{t('auth.email') || 'Email'}</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              disabled
              style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
            />
            <small style={{ color: '#666', fontSize: '0.875rem' }}>
              {t('dashboard.emailNote') || 'Email cannot be changed'}
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="firstName">{t('auth.firstName') || 'First Name'}</label>
            <input
              type="text"
              id="firstName"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              disabled={!isEditing}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="name">{t('auth.name') || 'Name'}</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              disabled={!isEditing}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="phone">{t('auth.phone') || 'Phone'}</label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              disabled={!isEditing}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="language">{t('nav.language') || 'Language'}</label>
            <select
              id="language"
              name="language"
              value={formData.language}
              onChange={handleChange}
              disabled={!isEditing}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #ffb366',
                borderRadius: '5px',
                fontSize: '1rem',
                color: '#132e48',
                backgroundColor: isEditing ? 'white' : '#f5f5f5',
                cursor: isEditing ? 'pointer' : 'not-allowed',
              }}
            >
              <option value="en">English</option>
              <option value="fr">Français</option>
              <option value="nl">Nederlands</option>
            </select>
          </div>

          {isEditing ? (
            <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
              <button type="submit" className="auth-button" disabled={loading} style={{ flex: 1 }}>
                {loading ? (t('auth.loading') || 'Loading...') : (t('dashboard.save') || 'Save Changes')}
              </button>
              <button 
                type="button" 
                onClick={handleCancel}
                className="auth-button" 
                disabled={loading}
                style={{ 
                  flex: 1, 
                  background: '#6c757d',
                }}
              >
                {t('dashboard.cancel') || 'Cancel'}
              </button>
            </div>
          ) : (
            <button 
              type="button" 
              onClick={() => setIsEditing(true)}
              className="auth-button"
              style={{ marginTop: '1rem' }}
            >
              {t('dashboard.edit') || 'Edit Profile'}
            </button>
          )}
        </form>

        <div style={{ marginTop: '2rem', paddingTop: '2rem', borderTop: '1px solid #ffb366' }}>
          <button 
            onClick={logout}
            className="auth-button"
            style={{ 
              background: '#dc3545',
            }}
          >
            {t('auth.logout') || 'Logout'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;

